import { ApiError } from './api';

/**
 * Extract per-field error messages from a backend 422.
 *
 * Backend zod validation errors arrive as:
 *   details.issues.fieldErrors: { [field_name]: string[] }
 * (zod's .flatten() shape). Returns the first message per field, keyed by
 * the snake_case field name — which matches our form state keys.
 *
 * Returns {} for non-ApiError or errors without field details, so callers
 * can unconditionally `set_field_errors(field_errors_from(err))`.
 */
export function field_errors_from(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  const issues = (err.details as { issues?: { fieldErrors?: unknown } } | undefined)
    ?.issues;
  const field_errors = issues?.fieldErrors;
  if (!field_errors || typeof field_errors !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(field_errors)) {
    if (Array.isArray(messages) && typeof messages[0] === 'string') {
      out[field] = messages[0];
    }
  }
  return out;
}

/**
 * Top-of-form message for a failed save.
 *
 * Only points at "the highlighted fields" when the caller actually renders
 * per-field errors — pass `highlights_fields: true` alongside a
 * `set_field_errors(field_errors_from(err))` call. Most screens don't, and
 * telling those users to fix highlighting that never appears leaves them with
 * no idea what went wrong.
 *
 * Otherwise falls back to `err.message`, which the API client already flattens
 * from `details.issues` into something readable
 * (e.g. `Min notice hours: Number must be less than or equal to 168`).
 */
export function form_error_message(
  err: unknown,
  fallback: string,
  opts: { highlights_fields?: boolean } = {},
): string {
  if (!(err instanceof ApiError)) return fallback;
  if (opts.highlights_fields && Object.keys(field_errors_from(err)).length > 0) {
    return 'Please fix the highlighted fields below.';
  }
  return err.message || fallback;
}
